import React from 'react';

import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowHorizontalFlow from './components/WorkflowHorizontalFlow';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  {
    label: 'Part2',
    title: 'API実装ワークフローとその全体像',
    path: '/devdocs/workflow/be-step2',
    active: true,
  },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const WorkflowBeStep2: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part2 — API実装ワークフローとその全体像"
      seriesName="バックエンド開発編"
      tags={['Backend', 'Workflow', 'api_project_rule']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part1: スキーマ定義+API仕様書', path: '/devdocs/workflow/be-step1' }}
      nextLink={{ label: 'Part3: Phase1 DD書の作成', path: '/devdocs/workflow/be-step3' }}
    >
      <WorkflowLearningPoints
        items={[
          'API実装ワークフローの全体像と3フェーズ構成',
          '「成果物作成→停止→確認」の入力プロトコル（0: / 1:）',
          'ワークフローの起動方法と複数API同時実行時の注意点',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'API実装ワークフロー とは' },
          { id: 'section-2', label: '3フェーズの全体像' },
          { id: 'section-3', label: 'ワークフローの共通ルール' },
          { id: 'section-4', label: '起動方法' },
          { id: 'section-5', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* セクション1: API実装ワークフロー とは */}
      <WorkflowSection title="API実装ワークフロー とは" id="section-1" number={1}>
        <WorkflowText>
          API実装ワークフロー
          は、API仕様書からDD書作成→コード生成→テスト実行までの3フェーズのワークフロー全体を定義したステアリングファイルです。まずは全文を見てみましょう。
        </WorkflowText>

        <WorkflowSection
          title="本プロジェクトで利用した api_project_rule.md の紹介"
          subNumber="1.1"
        >
          <WorkflowText>
            ワークフローの進行ルールを定義したSteeringファイルです。Front Matterは inclusion: manual
            を指定し、ユーザーがチャットで #api_project_rule.md と明示した時のみ読み込まれます。
          </WorkflowText>

          <WorkflowCodeBlock
            title="api_project_rule.md 全文を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API作成ワークフロー

## 共通ルール

- 設計書の確認時、修正時に明らかな誤字脱字があれば修正すること。
- 用語集.md と比較して物理名が合っていなければ用語集を正として設計書を修正する。
- 誤字、脱字かの判断が付かなければ修正前に確認すること。
- クレジットの消費を抑えるために時間がかかっても良いのでサブエージェントは使用しない。

### クレジット節約ルール

- Rv指摘ファイルの確認時は一覧ファイル（設計書Rv指摘.md / 実装Rv指摘.md）のみ読み込む。詳細ファイル（_詳細.md）は必要な指摘IDの内容を確認する場合のみ読み込む。
- Rv指摘ファイルへの追記時:
    - 一覧テーブルへの行追加は strReplace で該当行を特定して追加する。
    - 詳細の追記は insert（末尾追記）を使用し、全文読み込みを回避する。
- 既存コードの読み込みは同一パターンのクラスを1つ確認すれば十分。同パターンの複数ファイルを読まない。
- DD書テンプレートは過去に複数回生成しているため参照不要。直接作成する。
- instructions.md や mk_instructions.md の修正時は、差分が小さければ strReplace を使う。全面的な変更の場合のみ create で再作成する。

## 目的

- 指定された 30_API仕様書 配下にある当該API仕様書を完成させる。
    - API仕様書は複数指定可。
- diff.md と instructions.md を生成する。
- DD書を作成/修正する。
- 当該API仕様書とDD書から mk_instructions.md を生成する。
- コードを生成してAPIを実行する。

## 複数API仕様書を指定した場合のルール

- 各フェーズは全API仕様書まとめて進行する（APIごとに逐次実施しない）。
- 成果物の単位:

| 成果物 | 単位 | 備考 |
|---|---|---|
| 作業ディレクトリ | API仕様書ごとに1つ | APIID と同名のディレクトリを各API分作成する |
| diff.md | API仕様書ごとに1つ | 仕様書の差分を記録するため仕様書単位で作成する |
| instructions.md | 代表1つにのみ詳細を記載 | 他は代表 instructions.md へのリンクのみ記載する |
| DD書 | クラス単位（API単位ではない） | 複数APIで同じクラスを使う場合は既存DD書に追記する |
| mk_instructions.md | instructions.md と同じルール | 代表1つに詳細を記載し、他はリンクのみ |
| test.md | instructions.md と同じルール | 代表1つに詳細を記載し、他はリンクのみ |

## ワークフローの進め方

各フェーズは以下のサイクルで進行する:

1. 成果物の作成
2. 実行停止（ユーザーの確認待ち）
3. ユーザーがプロンプトで指示を入力する
4. 指示に応じて次のアクションを実行する

### ユーザーの入力プロトコル

| 入力 | 意味 | AIのアクション |
|---|---|---|
| \`0:\${コメント}\` | 成果物に問題なし | 次のフェーズに進む |
| \`1:\${コメント}\` | Rv指摘または注釈を追加した | 指摘・注釈を確認し修正した後、当該フェーズの成果物作成を再実施する |

### Rv指摘・注釈の処理ルール

- 指摘（\`note:rv\`）の場合:
    1. 指摘内容に従い成果物を修正する。
    2. 指摘記載先（\`nowrite\` の場合を除く）:
        - フェーズ1（設計書）での指摘 → 設計書Rv指摘.md
        - フェーズ2（コード生成）での指摘 → 実装Rv指摘.md
    3. 指摘を成果物から削除する。
- 注釈（\`note:info\` / \`note:warn\` / \`note:alert\`）の場合:
    1. 注釈内容を指示として instructions.md（または mk_instructions.md）に追加する。
    2. 追加した指示に基づき成果物を修正する。
    3. 注釈を成果物から削除する。
- 修正後は設計書Rv指摘.md の過去の指摘に該当する箇所がないか確認し、あれば修正する。

---

## フェーズ1: 設計書の作成/修正

### 1-1. 作業ディレクトリの作成

1. \`.kiro/specs\` に \`10_API\` ディレクトリを作成する（すでにあればスキップ）。
2. APIID と同名のディレクトリを作成する（すでにあればスキップ）。
3. 当該API仕様書の変更履歴の最新バージョンでディレクトリを作成する（すでにあればスキップ）。
4. そこを作業ディレクトリとする。

### 1-2. diff.md の作成

1. 当該API仕様書の変更履歴が v1 の場合: 新規作成としてAPIの概要を diff.md に記載する。
2. 当該API仕様書の変更履歴が v2 以降の場合: 修正としてAPIの修正内容の概要を diff.md に記載する。
    - 現在のブランチと \`develop\` ブランチのdiffを取得して修正差分を確認する。
    - 修正差分が分からなければ前回バージョンまでの diff.md と instructions.md を確認する。
3. 今回の変更点を記載する。
4. 当該API仕様書の変更履歴に diff.md へのリンクを追加する。
5. 設計書Rv指摘.md を確認して過去の指摘に該当する箇所があれば修正する。

#### diff.md 記載ルール

- プロパティの具体的な設定値は記載しない。プロパティキー名のみ参照する。
- 外部定義値の具体値を diff.md に含めない。

### 1-3. instructions.md の作成
...（以下略）

---

## フェーズ2: コードの生成

### 2-1. mk_instructions.md の作成
...（以下略）

---

## フェーズ3: テスト

### 3-1. test.md の作成
...（以下略）`}
          />

          <WorkflowInfoBox type="info">
            長いですが、要は以下の3フェーズに分かれています。Part3〜5で各フェーズを詳しく解説します。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2: 3フェーズの全体像 */}
      <WorkflowSection title="3フェーズの全体像" id="section-2" number={2}>
        <WorkflowHorizontalFlow
          steps={[
            {
              icon: <BuildIcon sx={{ fontSize: 20 }} />,
              label: 'Phase1 設計書作成',
              sub: 'diff / instructions / DD書',
            },
            {
              icon: <CodeIcon sx={{ fontSize: 20 }} />,
              label: 'Phase2 コード生成',
              sub: 'mk_instructions / 実装',
            },
            {
              icon: <DescriptionIcon sx={{ fontSize: 20 }} />,
              label: 'Phase3 単体テスト',
              sub: 'test.md / テスト実行',
            },
          ]}
        />
        <WorkflowTable
          headers={['フェーズ', '解説記事', '主な成果物']}
          rows={[
            ['Phase1: 設計書作成', 'Part3', 'diff.md, instructions.md, DD書'],
            ['Phase2: コード生成', 'Part4', 'mk_instructions.md, Javaソースコード'],
            ['Phase3: 単体テスト', 'Part5', 'test.md, test_result.md'],
          ]}
        />
      </WorkflowSection>

      {/* セクション3: ワークフローの共通ルール */}
      <WorkflowSection title="ワークフローの共通ルール" id="section-3" number={3}>
        <WorkflowText>全フェーズに共通する進め方：</WorkflowText>
        <WorkflowTable
          headers={['ルール', '説明']}
          rows={[
            [
              '成果物作成 → 停止 → 確認',
              '各ステップで成果物を作成後、AIは停止してユーザーの確認を待つ',
            ],
            [
              '入力プロトコル 0: / 1:',
              '0:コメント = 問題なし（次へ進む）、1:コメント = 指摘あり（修正して再実施）',
            ],
            [
              'Rv指摘・注釈の処理',
              'note:rv は指摘として修正＋Rv指摘.mdに記録。note:info/warn/alert は注釈として instructions に追加＋修正',
            ],
            ['用語集の参照', '物理名は常に用語集.mdと整合を取る'],
          ]}
        />

        <WorkflowSection title="成果物の単位（複数API指定時）" subNumber="3.1">
          <WorkflowTable
            headers={['成果物', '単位', '備考']}
            rows={[
              [
                '作業ディレクトリ',
                'API仕様書ごとに1つ',
                'APIID と同名のディレクトリを各API分作成する',
              ],
              ['diff.md', 'API仕様書ごとに1つ', '仕様書の差分を記録するため仕様書単位で作成する'],
              [
                'instructions.md',
                '代表1つにのみ詳細を記載',
                '他は代表 instructions.md へのリンクのみ記載する',
              ],
              [
                'DD書',
                'クラス単位（API単位ではない）',
                '複数APIで同じクラスを使う場合は既存DD書に追記する',
              ],
              [
                'mk_instructions.md',
                'instructions.md と同じルール',
                '代表1つに詳細を記載し、他はリンクのみ',
              ],
              ['test.md', 'instructions.md と同じルール', '代表1つに詳細を記載し、他はリンクのみ'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="入力プロトコルの詳細" subNumber="3.2">
          <WorkflowText>各フェーズは以下のサイクルで進行します：</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              '成果物の作成',
              '実行停止（ユーザーの確認待ち）',
              'ユーザーがプロンプトで指示を入力する',
              '指示に応じて次のアクションを実行する',
            ]}
          />
          <WorkflowTable
            headers={['入力', '意味', 'AIのアクション']}
            rows={[
              ['0:${コメント}', '成果物に問題なし', '次のフェーズに進む'],
              [
                '1:${コメント}',
                'Rv指摘または注釈を追加した',
                '指摘・注釈を確認し修正した後、当該フェーズの成果物作成を再実施する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Rv指摘・注釈の処理ルール" subNumber="3.3">
          <WorkflowText>指摘（note:rv）の場合：</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              '指摘内容に従い成果物を修正する',
              '指摘記載先（nowrite の場合を除く）: フェーズ1での指摘→設計書Rv指摘.md、フェーズ2での指摘→実装Rv指摘.md',
              '指摘を成果物から削除する',
            ]}
          />
          <WorkflowText>注釈（note:info / note:warn / note:alert）の場合：</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              '注釈内容を指示として instructions.md（または mk_instructions.md）に追加する',
              '追加した指示に基づき成果物を修正する',
              '注釈を成果物から削除する',
            ]}
          />
          <WorkflowText>
            修正後は設計書Rv指摘.md の過去の指摘に該当する箇所がないか確認し、あれば修正します。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: 起動方法 */}
      <WorkflowSection title="起動方法" id="section-4" number={4}>
        <WorkflowText>Kiroのチャットで以下のように指示します：</WorkflowText>
        <WorkflowChatBubble role="human">
          #api_project_rule.md{'\n\n'}
          API3001 の仕様書を対象にワークフローを開始してください。
        </WorkflowChatBubble>

        <WorkflowInfoBox type="warning">
          基本は1API単位で実行してください。
          {'\n\n'}
          ワークフローは複数APIを同時に指定することも可能ですが、同時に扱うAPIが増えるほどAIのコンテキストが分散し、DD書やコードの品質が下がります。特に初めて触る機能領域では1APIずつ実行して、レビューで品質を確認しながら進めることを推奨します。
          {'\n\n'}
          複数API同時指定が有効なケース:
          同一リソースのCRUD（登録・更新・取得・一覧）を一括で作る場合など、共通部分が多く方針が明確な場合に限ります。
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション5: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-5" number={5}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '1API単位で実行し、レビューで品質を安定させる',
              effect:
                'コンテキストが絞られるためAIの出力精度が高くなる。慣れた機能領域のみ複数APIにする',
            },
            {
              practice: '0: / 1: に具体的なコメントを添える',
              effect:
                'AIが修正意図を正確に把握でき、的外れな修正を防げる。1: だけだと何を直せばよいか曖昧になる',
            },
            {
              practice: '指摘は note:rv で即座に書き込む',
              effect:
                '後でまとめて指摘しようとすると忘れる。気づいた時に書いておけばAIが確実に拾う',
            },
            {
              practice: '各停止ポイントで成果物を必ず目視確認する',
              effect:
                'AIの自動進行に任せると方針ズレが蓄積する。停止ポイントは品質ゲートとして活用する',
            },
            {
              practice: '同一リソースのCRUDは複数API同時指定を検討する',
              effect:
                'テーブル設計・Model・Repositoryが共通のため、一括で作ると整合性が自然に取れる',
            },
            {
              practice: 'ワークフロー起動前にAPI仕様書を完成させておく',
              effect: '処理概要やI/Fが未確定のまま起動すると、Phase1で手戻りが多発する',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '関連性の薄い複数APIを同時に指定する',
              effect:
                'AIのコンテキストが分散し、DD書やコードの品質が低下する。共通部分が多いAPIに限定して同時指定する',
            },
            {
              practice: '停止ポイントを 0: で素通りする',
              effect:
                '方針ズレに気づかないまま後続フェーズに進み、大規模な手戻りが発生する。最低限 diff.md / instructions.md の方針を確認する',
            },
            {
              practice: '1: で指摘したのに成果物から note:rv を消し忘れる',
              effect:
                'AIが次のサイクルで再度同じ指摘を処理しようとして混乱する。指摘処理後にAIが自動削除するのを確認する（ルール通り）',
            },
            {
              practice: 'ワークフロー途中でAPI仕様書のI/Fを変更する',
              effect:
                '既に作成済みのDD書・instructionsとの整合性が崩れる。I/F変更はワークフロー開始前に確定させる。やむを得ない場合は Phase1 からやり直す',
            },
            {
              practice: 'Rv指摘せずにDD書やコードを直接手修正する',
              effect:
                'Rv指摘.md に記録が残らず、同じミスが再発する。ルール追加の機会も逃す。note:rv で記載してAIに修正させる',
            },
            {
              practice: 'フェーズを飛ばして実行する（例: Phase1を省略してPhase2から）',
              effect:
                'DD書がない状態でコード生成されるため品質が保証されない。必ず Phase1 → Phase2 → Phase3 の順序を守る',
            },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep2;
